import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function InviteClaim() {
  const { token } = useParams();
  const [check, setCheck] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/invites/${token}`)
      .then(r => setCheck(r.data))
      .catch(() => setCheck({ valid: false, reason: 'Could not validate this invite link.' }));
  }, [token]);

  const claim = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/invites/${token}/redeem`, form);
      localStorage.setItem('sls_token', r.data.token);
      localStorage.setItem('sls_user', JSON.stringify(r.data.user));
      toast.success('Trial account created — welcome!');
      window.location.href = '/prove-it';
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not claim invite');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="invite-claim-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-4">
            <i className="fa-solid fa-solar-panel text-white text-xl" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white">Samaritan Solar Investor Trial</h1>
          <p className="text-sm text-slate-400 mt-1">You've been invited to test the platform with real, live leads</p>
        </div>

        {check === null ? (
          <div className="text-center text-slate-400 py-10"><i className="fa-solid fa-circle-notch fa-spin text-xl" /></div>
        ) : !check.valid ? (
          <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 text-center" data-testid="invite-invalid">
            <i className="fa-solid fa-link-slash text-red-400 text-2xl mb-3 block" />
            <p className="text-sm text-slate-300">{check.reason}</p>
            <p className="text-xs text-slate-500 mt-2">Ask your Samaritan Solar contact for a fresh link.</p>
          </div>
        ) : (
          <form onSubmit={claim} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4" data-testid="invite-claim-form">
            <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2">
              <i className="fa-solid fa-clock" />
              Valid until {new Date(check.expires_at).toLocaleDateString()} · {check.limits?.scans} live scans + {check.limits?.enrichments} contact verifications included
            </div>
            <input required placeholder="Your name" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
              data-testid="invite-name-input" />
            <input required type="email" placeholder="Work email" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
              data-testid="invite-email-input" />
            <input required type="password" minLength={6} placeholder="Choose a password (6+ chars)" value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
              data-testid="invite-password-input" />
            <button type="submit" disabled={submitting}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-sm hover:brightness-110 active:scale-[0.99] transition-all disabled:opacity-60"
              data-testid="invite-claim-btn">
              {submitting ? <i className="fa-solid fa-circle-notch fa-spin mr-2" /> : <i className="fa-solid fa-rocket mr-2" />}
              Start My Trial
            </button>
            <p className="text-[11px] text-slate-500 text-center">You'll land directly on the live "Prove It" flow.</p>
          </form>
        )}
      </div>
    </div>
  );
}
